import React, { useEffect, useState } from "react";
import { adminApi, goToGoogleLogin } from "./api";
import Dashboard from "./Dashboard";
import LeadsTable from "./LeadsTable";
import Settings from "./Settings";
import { LayoutDashboard, Users, Settings as Cog, LogOut, Loader2, ShieldAlert, BrainCircuit } from "lucide-react";

export default function MasterAdmin() {
  const [status, setStatus] = useState("loading"); // loading | login | authed | denied
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [leadsFilters, setLeadsFilters] = useState({});
  const [leadsKey, setLeadsKey] = useState(0);

  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      const sid = decodeURIComponent(m[1]);
      window.history.replaceState(null, "", window.location.pathname);
      adminApi.session(sid)
        .then((u) => { setUser(u); setStatus("authed"); })
        .catch((e) => { setStatus(e.status === 403 ? "denied" : "login"); });
      return;
    }
    adminApi.me()
      .then((u) => { setUser(u); setStatus("authed"); })
      .catch((e) => { setStatus(e.status === 403 ? "denied" : "login"); });
  }, []);

  const logout = async () => { try { await adminApi.logout(); } catch (e) { /* ignore */ } setStatus("login"); setUser(null); };
  const goLeads = (filters) => { setLeadsFilters(filters || {}); setLeadsKey((k) => k + 1); setTab("leads"); };

  if (status === "loading") return <Screen><Loader2 className="h-8 w-8 animate-spin text-violet-500" /></Screen>;

  if (status === "login" || status === "denied") {
    return (
      <Screen>
        <div className="w-full max-w-sm rounded-3xl bg-white border border-slate-200 p-8 text-center shadow-xl" data-testid="admin-login">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl cta-gradient text-white"><BrainCircuit className="h-7 w-7" /></span>
          <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Master Admin</h1>
          <p className="mt-1 text-[14px] text-slate-500">MapMyKid internal leads console</p>
          {status === "denied" && (
            <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 p-3 text-[13px] text-rose-700 flex items-start gap-2" data-testid="admin-denied">
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" /> This account is not authorized for MapMyKid Master Admin.
            </div>
          )}
          <button onClick={goToGoogleLogin} data-testid="google-login-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] font-semibold text-slate-700 hover:bg-slate-50">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" /> Sign in with Google
          </button>
          <p className="mt-4 text-[12px] text-slate-400">Access restricted to approved MapMyKid accounts.</p>
        </div>
      </Screen>
    );
  }

  const nav = [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["leads", "Leads", Users],
    ["settings", "Settings", Cog],
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-shell">
      <header className="sticky top-0 z-30 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg cta-gradient"><BrainCircuit className="h-4.5 w-4.5" /></span>
            <span className="font-extrabold">MapMyKid <span className="text-slate-400 font-semibold">Master Admin</span></span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[13px] text-slate-300">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-[13px] font-semibold hover:bg-white/20"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
        <nav className="mx-auto max-w-7xl px-4 sm:px-6 flex gap-1 -mb-px">
          {nav.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`nav-${k}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-semibold border-b-2 transition-colors ${tab === k ? "border-violet-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
        {tab === "dashboard" && <Dashboard goLeads={goLeads} />}
        {tab === "leads" && <LeadsTable key={leadsKey} initialFilters={leadsFilters} />}
        {tab === "settings" && <Settings />}
      </main>
    </div>
  );
}

const Screen = ({ children }) => (
  <div className="min-h-screen grid place-items-center bg-slate-100 p-4">{children}</div>
);
